<template>
  <div class="container">
    <div v-if="loading" class="modern-loading-wrapper">
      <div class="modern-loader"></div>
      <div class="loading-text">Loading dealer group details...</div>
    </div>

    <div v-else>
      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">
            <i class="fa fa-info-circle text-dark mr-2"></i> Group Details
          </h4>
        </div>
        
        <div class="detail-grid">
          <div class="detail-item">
            <label>Group Name</label>
            <div class="value">{{ dealerGroup.name }}</div>
          </div>
          <div class="detail-item">
            <label>Status</label>
            <div>
              <span :class="dealerGroup.is_active == 1 ? 'status-badge active' : 'status-badge inactive'">
                {{ dealerGroup.is_active == 1 ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
          <div class="detail-item">
            <label>Created At</label>
            <div class="value">{{ formatDateTime(dealerGroup.created_at) }}</div>
          </div>
          <div class="detail-item full-width">
            <label>Description</label>
            <div class="value description-text">{{ dealerGroup.description || 'No description provided' }}</div>
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">
            <i class="fa fa-users text-dark mr-2"></i> Dealers in this Group
          </h4>
          
          <div class="search-container">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search by dealer name..." 
              class="form-control search-input"
            />
            <i class="fa fa-search search-icon"></i>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Name</th>
                <th>Address</th>
                <th>City</th>
                <th class="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredDealers" :key="dealer.id">
                <td>{{ dealer.business_name }}</td>
                <td>{{ dealer.address1 }}</td>
                <td>{{ dealer.city }}</td> 
                <td class="text-center">
                  <span :class="dealer.is_active == 1 ? 'status-badge active' : 'status-badge inactive'">
                    {{ dealer.is_active == 1 ? 'Active' : 'Inactive' }}
                  </span>
                </td>
              </tr>
              <tr v-if="filteredDealers.length === 0">
                <td colspan="4" class="text-center text-muted">
                  {{ searchQuery ? 'No dealers found matching your search.' : 'No dealers assigned to this group.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { viewDealerGroupDetails } from "@/services/dataService";

export default {
  name: "ViewDealerGroup",
  props: {
    dealerGroupId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      token: localStorage.getItem("token_set") || "",
      searchQuery: "",
      dealerGroup: {},
      dealers: []
    };
  },
  computed: {
    filteredDealers() {
      if (!this.searchQuery) {
        return this.dealers;
      }
      const query = this.searchQuery.toLowerCase();
      return this.dealers.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query) ||
        (dealer.city || '').toLowerCase().includes(query)
      );
    }
  },
  mounted() {
    this.fetchDealerGroupDetails();
  },
  methods: {
    async fetchDealerGroupDetails() {
      try {
        if (!this.dealerGroupId) {
          console.error("Dealer Group ID is missing");
          this.loading = false;
          return;
        }
        this.loading = true;
        const response = await viewDealerGroupDetails(this.dealerGroupId, this.token);
        
        if (response.data && response.data.data && response.data.data.groupDetails && response.data.data.groupDealers) {
          this.dealerGroup = response.data.data.groupDetails;
          this.dealers = response.data.data.groupDealers;
        } else {
          this.dealerGroup = {};
          this.dealers = [];
        }
      } catch (error) {
        console.error("Error fetching dealer group details:", error);
        this.dealerGroup = {};
        this.dealers = [];
      } finally {
        this.loading = false;
      }
    },

    // Format DateTime
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-';
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString; // Return original if invalid
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const strHours = String(hours).padStart(2, '0');
      
      return `${year}-${month}-${day} ${strHours}:${minutes} ${ampm}`;
    }
  }
};
</script>

<style scoped>
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 100%;
}

.section-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 20px;
  background-color: #fff;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 5px;
  flex-wrap: wrap;
  gap: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.mr-2 {
  margin-right: 0.5rem;
}

.search-container {
  position: relative;
  width: 300px;
  max-width: 100%;
}

.search-input {
  width: 100%;
  padding: 8px 30px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 14px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item.full-width {
  grid-column: 1 / -1;
  margin-top: 10px;
  padding-top: 15px;
  border-top: 1px dashed #eee;
}

.description-text {
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
}

.detail-item label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 600;
}

.detail-item .value {
  font-size: 15px;
  font-weight: 500;
  color: #212529;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  min-width: 80px; 
  text-align: center;
}

.status-badge.active { 
  background-color: #d4edda; 
  color: #155724; 
}

.status-badge.inactive { 
  background-color: #f8d7da; 
  color: #721c24; 
}

.table-responsive { 
  overflow-x: auto; 
  margin-top: 15px; 
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th, .table td {
  padding: 12px 15px;
  border: 1px solid #dee2e6;
  text-align: left;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
}

.text-center { 
  text-align: center; 
}

.text-muted { 
  color: #6c757d; 
  font-style: italic; 
  padding: 20px; 
}

.mb-0 { 
  margin-bottom: 0 !important; 
}

.modern-loading-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
}

.modern-loader {
  width: 48px;
  height: 48px;
  border: 3px solid #eef2f7;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: modern-spin 1s cubic-bezier(0.5, 0.1, 0.4, 0.9) infinite;
}

.loading-text {
  margin-top: 20px;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  letter-spacing: 0.3px;
  animation: modern-pulse 1.5s ease-in-out infinite;
}

@keyframes modern-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes modern-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>